import { Toggle, InputBox } from "@components/index";
import useAddClientFormData from "./addClientFormData";
import "./index.scss";

const AddClientForm = ({ onCancel, clientData = null, onClearFilters }) => {
  const isEditMode = Boolean(clientData);

  const {
    formData,
    isSubmitting,
    handleChange,
    handleBlur,
    handleSubmit,
    handleCancel,
    getFieldError,
    hasFieldError,
    hasFormChanged,
  } = useAddClientFormData({ onCancel, clientData, onClearFilters });

  return (
    <div className="add-client-form-modal">
      {/* Modal Header */}
      <div className="add-client-form-modal__header">
        <div className="add-client-form-modal__header-content">
          <div className="add-client-form-modal__header-text">
            <h2>{isEditMode ? "Edit Client" : "Add New Client"}</h2>
            <p>
              {isEditMode
                ? "Update client information"
                : "Create a new client account"}
            </p>
          </div>
          {/* Toggle in header - Only show in edit mode */}
          {isEditMode && (
            <div className="add-client-form-modal__header-toggle">
              <Toggle
                checked={formData.is_active}
                onChange={(checked) =>
                  handleChange({
                    target: {
                      name: "is_active",
                      value: checked,
                    },
                  })
                }
                label={"Status"}
                labelPosition="left"
                size="medium"
                color="success"
              />
            </div>
          )}
        </div>
      </div>

      {/* Modal Body */}
      <div className="add-client-form-modal__body">
        <form className="add-client-form-modal__form" onSubmit={handleSubmit}>
          {/* Row 1: Client Name */}
          <div className="add-client-form-modal__form-row">
            <div className="add-client-form-modal__form-group add-client-form-modal__form-group--full">
              <InputBox
                label="Client Name"
                name="client_name"
                value={formData.client_name}
                onChange={handleChange}
                onBlur={handleBlur}
                placeholder="Enter client name"
                error={hasFieldError("client_name")}
                helperText={getFieldError("client_name")}
                required
                fullWidth
                disabled={isEditMode}
                className="add-client-form-input"
              />
            </div>
          </div>

          {/* Row 2: Opening CD Balance */}
          <div className="add-client-form-modal__form-row">
            <div className="add-client-form-modal__form-group">
              <InputBox
                label="Opening Claims CD Balance"
                type="number"
                name="opening_cd_balance"
                value={formData.opening_cd_balance}
                onChange={handleChange}
                onBlur={handleBlur}
                placeholder="Enter Claims CD balance"
                error={hasFieldError("opening_cd_balance")}
                helperText={getFieldError("opening_cd_balance")}
                required
                fullWidth
                disabled={isEditMode}
                className="add-client-form-input"
                inputMode="numeric"
              />
            </div>

            <div className="add-client-form-modal__form-group">
              <InputBox
                label="Opening Premium CD Balance"
                type="number"
                name="opening_cd_balance_premium"
                value={formData.opening_cd_balance_premium}
                onChange={handleChange}
                onBlur={handleBlur}
                placeholder="Enter Premium CD balance"
                error={hasFieldError("opening_cd_balance_premium")}
                helperText={getFieldError("opening_cd_balance_premium")}
                required
                fullWidth
                disabled={isEditMode}
                className="add-client-form-input"
                inputMode="numeric"
              />
            </div>
          </div>

          {/* Row 3: Maximum Voucher Amount */}
          <div className="add-client-form-modal__form-row">
            <div className="add-client-form-modal__form-group add-client-form-modal__form-group--full">
              <InputBox
                label="Maximum Voucher Amount (in INR)"
                type="number"
                name="maximum_voucher_value"
                value={formData.maximum_voucher_value}
                onChange={handleChange}
                onBlur={handleBlur}
                placeholder="Enter maximum voucher amount"
                error={hasFieldError("maximum_voucher_value")}
                helperText={getFieldError("maximum_voucher_value")}
                required
                fullWidth
                className="add-client-form-input"
                inputMode="numeric"
                min="1"
                step="1"
              />
            </div>
          </div>

          {/* Row 4: Claim Settings */}
          <div className="add-client-form-modal__form-row">
            <div className="add-client-form-modal__form-group">
              <InputBox
                label="Maximum time for Claim Intimation (in hours)"
                type="number"
                name="claim_intimation_timeline"
                value={formData.claim_intimation_timeline}
                onChange={handleChange}
                onBlur={handleBlur}
                placeholder="Enter maximum hours"
                error={hasFieldError("claim_intimation_timeline")}
                helperText={getFieldError("claim_intimation_timeline")}
                required
                fullWidth
                className="add-client-form-input"
                inputMode="numeric"
                min="1"
                step="1"
              />
            </div>
            <div className="add-client-form-modal__form-group">
              <InputBox
                label="Maximum Voucher Validity (in Days)"
                type="number"
                name="voucher_validity_timeline"
                value={formData.voucher_validity_timeline}
                onChange={handleChange}
                onBlur={handleBlur}
                placeholder="Enter validity days"
                error={hasFieldError("voucher_validity_timeline")}
                helperText={getFieldError("voucher_validity_timeline")}
                required
                fullWidth
                className="add-client-form-input"
                inputMode="numeric"
                min="1"
                step="1"
              />
            </div>
          </div>
        </form>
      </div>

      {/* Modal Actions */}
      <div className="add-client-form-modal__actions">
        <button
          type="button"
          onClick={handleCancel}
          className="add-client-form-modal__button add-client-form-modal__button--secondary"
          disabled={isSubmitting}
        >
          Cancel
        </button>
        <button
          type="submit"
          onClick={handleSubmit}
          className={`add-client-form-modal__button add-client-form-modal__button--primary ${
            isSubmitting ? "add-client-form-modal__button--loading" : ""
          }`}
          disabled={isSubmitting || (isEditMode && !hasFormChanged)}
        >
          {isSubmitting ? "" : isEditMode ? "Update Client" : "Add Client"}
        </button>
      </div>
    </div>
  );
};

export default AddClientForm;
